// ============================================
// ProductPicker
// Ô tìm & chọn MỘT sản phẩm — dùng chung cho trang Lô (#32) và Kiểm kê (#30)
// ============================================
//
// Không dùng SelectDropdown: nó nạp sẵn toàn bộ options, mà catalog cửa hàng có
// hàng nghìn mặt hàng. Ở đây tìm theo `search` phía server rồi chỉ hiện vài kết
// quả đầu.
//
// Sau khi chọn thì ô tìm kiếm được thay bằng một thẻ tóm tắt: cả hai màn dùng
// nó đều gửi `product_id` lên server, nên phải thấy rõ ĐANG chọn cái nào chứ
// không để lại một ô text mà người dùng tưởng là đã chọn theo chữ đang gõ.

import { useMemo, useState } from 'react';
import type { Product } from '@conai/shared-types';
import { useI18n } from '../../../i18n';
import { SearchInput } from '../../../components/shared';
import { useDebounce, useProducts } from '../../../hooks';
import { extractList } from '../../../utils/apiList';

export interface PickedProduct {
  id: string;
  name: string;
  sku: string;
}

interface ProductPickerProps {
  selected: PickedProduct | null;
  onSelect: (product: PickedProduct | null) => void;
  /** Giới hạn tìm kiếm trong catalog của một cửa hàng. */
  storeId?: string;
  placeholder?: string;
  /** Bộ lọc thì cho bỏ chọn; form bắt buộc thì vẫn cho, để đổi sang mặt hàng khác. */
  clearable?: boolean;
}

const MAX_RESULTS = 8;

export function ProductPicker({
  selected,
  onSelect,
  storeId,
  placeholder,
  clearable = true,
}: ProductPickerProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 400);

  const { data, isLoading } = useProducts({
    search: debouncedQuery || undefined,
    store: storeId,
    page: 1,
    page_size: MAX_RESULTS,
  });

  const results = useMemo(() => extractList<Product>(data).slice(0, MAX_RESULTS), [data]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{selected.name}</p>
          <p className="truncate text-xs text-slate-500">{selected.sku}</p>
        </div>
        {clearable && (
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              setQuery('');
            }}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
          >
            {t('productPickerChange')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder={placeholder ?? t('searchProducts')}
        className="w-full"
        width="auto"
      />
      {debouncedQuery.trim().length > 0 && (
        <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white">
          {isLoading && (
            <p className="px-3 py-2 text-xs text-slate-400">{t('loading')}</p>
          )}
          {!isLoading && results.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-400">{t('productPickerNoResults')}</p>
          )}
          {results.map((product) => (
            <button
              key={product.id}
              type="button"
              onClick={() => {
                onSelect({ id: product.id, name: product.name, sku: product.sku });
                setQuery('');
              }}
              className="flex w-full flex-col items-start px-3 py-2 text-left transition-colors hover:bg-slate-50"
            >
              <span className="text-sm font-medium text-slate-800">{product.name}</span>
              <span className="text-xs text-slate-500">{product.sku}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
